//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { configureStore, ThunkAction, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit'

import { ActivityEnum, RoleEnum } from '@shared/Enums'

import { poolActions, poolReducer, PoolState } from './poolSlice'

import AlertSlice, { alertActions, AlertState } from './alertSlice'
import { chainSlice, chainActions, ChainState, ChainStateInitialized } from './chainSlice'
import navSlice, { NavState } from './navSlice'
import apySlice, {
  reservesReducer,
  fetchCurrentAPY,
  fetchAPYGraphData,
  fetchReserves,
  clearReservesCache,
} from './apySlice'

import { logOT } from '@shared/loggingShared'

import { AlertCTO, alertCTOFromError, EthersError, UserError } from '@shared/AlertCTO'
import { Dispatch, useEffect, useState } from 'react'
import { useDispatch, useSelector, useStore } from 'react-redux'

import { fetchChainTimestampDelta } from '@react/chainThunks/fetchChainTimestampDeltaTx'
import { fetchPerimeterAddresses } from '@react/chainThunks/fetchPerimeterAddressesTx'
import { loginDirectToChainThunk } from '@react/chainThunks/loginToChainThunk'
import { fetchPoolOverviewCTO, fetchPoolOverviewsByChain } from '@react/fetchers/PoolFetchers'
import { UserConnection } from '@shared/DeploymentConfigTypes'
import { ethers } from 'ethers'
import { createClientWebsocketHandler } from './wsClient'

export const reducers = {
  poolState: poolReducer,

  alertState: AlertSlice.reducer,
  navState: navSlice.reducer,
  chainState: chainSlice.reducer,
  apy: apySlice,
  reserves: reservesReducer,
}
export const context = {
  dispatch: (_action: unknown) => {
    logOT.debug('dispatch')
  },
  getState: (): any => {
    logOT.debug('GetState')
    return {}
  },
}
export interface OtStoreState {
  poolState: PoolState

  alertState: AlertState
  navState: NavState
  chainState: ChainState
  apy: ReturnType<typeof apySlice>
  reserves: ReturnType<typeof reservesReducer>
}

// eslint-disable-next-line
const store = configureStore({
  reducer: reducers,

  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),

  // middleware: (getDefaultMiddleware) =>
  //   getDefaultMiddleware(
  //     {
  //       serializableCheck: {
  //         // Ignore these action types
  //         ignoredActions: [web3Actions.web3ConnectGanacheSucceed.toString(), web3Actions.metaMaskConnectSuccess.toString()],
  //         // Ignore these paths in the state
  //         ignoredPaths: ['web3State.web3', 'web3State.web3']
  //       }
  //     }
  //     ).concat(api.middleware).concat(sagaMiddleware).concat(thunk),
})

export async function setupLytStoreWithInitialStateWithChainConnection(store: OtStore): Promise<OtStore> {
  const { chainConfiguration, stageConfiguration } = store.getState().chainState
  logOT.info('setupStoreWithInitialStateWithChainConnection', chainConfiguration.frontendProviderUrl)
  const providerReadOnly = new ethers.JsonRpcProvider(chainConfiguration.frontendProviderUrl)

  // Create websocket provider (skip in test environments)
  let websocketProvider: ethers.WebSocketProvider | undefined

  if (process.env.NODE_ENV === 'test' || process.env.JEST_WORKER_ID) {
    logOT.info('Skipping WebSocket provider creation in test environment')
  } else {
    try {
      websocketProvider = new ethers.WebSocketProvider(
        chainConfiguration.frontendWebSocketProviderUrl,
        new ethers.Network(chainConfiguration.chainName, chainConfiguration.chainId)
      )
      logOT.info('WebSocket provider created successfully')
    } catch (error) {
      logOT.error('Failed to create WebSocket provider:', error)
      // Continue without websocket provider - the app should still work with HTTP provider
    }
  }

  logOT.info('setupStoreWithInitialStateWithChainConnection2')
  const perimeterAddresses = await fetchPerimeterAddresses(
    chainConfiguration,
    stageConfiguration.serverApiBaseUrl,
    stageConfiguration.restAPIKey,
    stageConfiguration.isLyt
  )
  const chainTimestampDelta = await fetchChainTimestampDelta(providerReadOnly)
  store.dispatch(
    chainSlice.actions.connectChainConfiguration({
      chainConfiguration,
      providerReadOnly,
      websocketProvider,
      chainTimestampDelta,
      perimeterAddresses,
    })
  )
  logOT.info('setupStoreWithInitialStateWithChainConnection2')
  // get vaults for chainConfiguration
  const lytPoolAddr = stageConfiguration.lytPoolAddr
  if (lytPoolAddr) {
    // Set current chain ID before fetching pool data (for first load)
    store.dispatch(poolActions.setCurrentChainId({ chainId: chainConfiguration.chainConfigurationName }))

    const poolOverviewDTO = await fetchPoolOverviewCTO(
      stageConfiguration.serverApiBaseUrl,
      stageConfiguration.restAPIKey,
      lytPoolAddr
    )
    if (poolOverviewDTO) {
      store.dispatch(
        poolActions.setPoolContractCTOList({
          poolContractCTOs: [poolOverviewDTO],
          chainId: chainConfiguration.chainConfigurationName,
        })
      )
    }
  }

  logOT.info('setupStoreWithInitialStateWithChainConnection3', stageConfiguration.userConnections)
  if (stageConfiguration.userConnections && stageConfiguration.userConnections.length > 0) {
    const USER_STORAGE_KEY = 'directUserAddr'
    const accountAddr = localStorage.getItem(USER_STORAGE_KEY) ?? stageConfiguration.userConnections[0].accountAddr
    if (accountAddr) {
      const userConnection: UserConnection | undefined = stageConfiguration.userConnections.find(
        (uc: UserConnection) => uc.accountAddr === accountAddr
      )

      logOT.info('setupStoreWithInitialStateWithChainConnection4', userConnection)
      store.dispatch(loginDirectToChainThunk(userConnection as UserConnection))
      let attempts = 0
      const maxAttempts = 50 // 5 seconds total max wait
      const waitForUserConnection = async () => {
        while (attempts < maxAttempts) {
          if (store.getState().chainState.userConnection) {
            logOT.info('setupStoreWithInitialStateWithChainConnection5', store.getState().chainState.userConnection)
            break
          }
          await new Promise((resolve) => setTimeout(resolve, 100)) // Wait 100ms
          attempts++
        }
      }
      await waitForUserConnection()
    }
  }

  logOT.info('setupStoreWithInitialStateWithChainConnection2', chainConfiguration.frontendProviderUrl)

  // Preload APY data for LYT pools
  if (lytPoolAddr) {
    const accountAddr = store.getState().chainState.userConnection?.accountAddr

    store.dispatch(
      fetchCurrentAPY({
        serverApiBaseUrl: stageConfiguration.serverApiBaseUrl,
        restAPIKey: stageConfiguration.restAPIKey,
        poolAddr: lytPoolAddr,
        accountAddr,
      })
    )

    store.dispatch(
      fetchAPYGraphData({
        serverApiBaseUrl: stageConfiguration.serverApiBaseUrl,
        restAPIKey: stageConfiguration.restAPIKey,
        poolAddr: lytPoolAddr,
        timeFilter: '1Y',
        accountAddr,
      })
    )

    store.dispatch(clearReservesCache())
    store.dispatch(
      fetchReserves({
        serverApiBaseUrl: stageConfiguration.serverApiBaseUrl,
        restAPIKey: stageConfiguration.restAPIKey,
        poolAddr: lytPoolAddr,
      })
    )
  }

  // Mark as initialized after all data is loaded
  store.dispatch(poolActions.finishedInitializing())

  return store
}
export async function setupStoreWithInitialStateWithChainConnection(store: OtStore): Promise<OtStore> {
  // const store = setupStoreWithInitialState()
  const { chainConfiguration, stageConfiguration } = store.getState().chainState
  logOT.info('setupStoreWithInitialStateWithChainConnection', chainConfiguration.frontendProviderUrl)
  const providerReadOnly = new ethers.JsonRpcProvider(chainConfiguration.frontendProviderUrl)

  // Create websocket provider (skip in test environments)
  let websocketProvider: ethers.WebSocketProvider | undefined

  if (process.env.NODE_ENV === 'test' || process.env.JEST_WORKER_ID) {
    logOT.info('Skipping WebSocket provider creation in test environment')
  } else {
    try {
      websocketProvider = new ethers.WebSocketProvider(
        chainConfiguration.frontendWebSocketProviderUrl,
        new ethers.Network(chainConfiguration.chainName, chainConfiguration.chainId)
      )
      logOT.info('WebSocket provider created successfully')
    } catch (error) {
      logOT.error('Failed to create WebSocket provider:', error)
      // Continue without websocket provider - the app should still work with HTTP provider
    }
  }

  logOT.info('setupStoreWithInitialStateWithChainConnection2')
  const perimeterAddresses = await fetchPerimeterAddresses(
    chainConfiguration,
    stageConfiguration.serverApiBaseUrl,
    stageConfiguration.restAPIKey,
    stageConfiguration.isLyt
  )
  const chainTimestampDelta = await fetchChainTimestampDelta(providerReadOnly)
  store.dispatch(
    chainSlice.actions.connectChainConfiguration({
      chainConfiguration,
      providerReadOnly,
      websocketProvider,
      chainTimestampDelta,
      perimeterAddresses,
    })
  )

  // Initialize ethers WebSocket connection status for LYT builds
  if (stageConfiguration.isLyt && websocketProvider) {
    // Test the connection and set initial status
    websocketProvider
      .getNetwork()
      .then(() => {
        store.dispatch(chainActions.ethersWsConnect())
        logOT.info('EthersWebSocketProvider initial connection successful')
      })
      .catch((error) => {
        logOT.error('EthersWebSocketProvider initial connection failed:', error)
        store.dispatch(chainActions.ethersWsDisconnect())
      })
  }

  logOT.info('setupStoreWithInitialStateWithChainConnection2')

  // Set current chain ID before fetching pool data (for first load)
  store.dispatch(poolActions.setCurrentChainId({ chainId: chainConfiguration.chainConfigurationName }))

  // get vaults for chainConfiguration
  const poolContractCTOList = await fetchPoolOverviewsByChain(
    stageConfiguration.serverApiBaseUrl,
    stageConfiguration.restAPIKey,
    chainConfiguration.chainConfigurationName
  )
  logOT.info('pools overview loaded', poolContractCTOList)
  store.dispatch(
    poolActions.setPoolContractCTOList({
      poolContractCTOs: poolContractCTOList,
      chainId: chainConfiguration.chainConfigurationName,
    })
  )

  if (stageConfiguration.websocketUrl?.length > 0) {
    await createClientWebsocketHandler(
      stageConfiguration.websocketUrl,
      store.dispatch,
      chainConfiguration.chainConfigurationName
    )
  }

  logOT.info('setupStoreWithInitialStateWithChainConnection3', stageConfiguration.userConnections)
  if (stageConfiguration.userConnections && stageConfiguration.userConnections.length > 0) {
    const USER_STORAGE_KEY = 'directUserAddr'
    const accountAddr = localStorage.getItem(USER_STORAGE_KEY) ?? stageConfiguration.userConnections[0].accountAddr
    if (accountAddr) {
      const userConnection: UserConnection | undefined = stageConfiguration.userConnections.find(
        (uc: UserConnection) => uc.accountAddr === accountAddr
      )

      logOT.info(
        'setupStoreWithInitialStateWithChainConnection4',
        stageConfiguration.userConnections,
        accountAddr,
        userConnection
      )
      store.dispatch(loginDirectToChainThunk(userConnection as UserConnection))
      let attempts = 0
      const maxAttempts = 50 // 5 seconds total max wait
      const waitForUserConnection = async () => {
        while (attempts < maxAttempts) {
          if (store.getState().chainState.userConnection) {
            logOT.info('setupStoreWithInitialStateWithChainConnection5', store.getState().chainState.userConnection)
            break
          }
          await new Promise((resolve) => setTimeout(resolve, 100)) // Wait 100ms
          attempts++
        }
      }
      await waitForUserConnection()
    }
  }

  logOT.info('setupStoreWithInitialStateWithChainConnection2', chainConfiguration.frontendProviderUrl)
  // Mark as initialized after all data is loaded
  store.dispatch(poolActions.finishedInitializing())

  return store
}
export const setupStoreWithInitialState = (preloadedState?: OtStoreState): OtStore => {
  return configureStore({
    reducer: reducers,
    preloadedState: preloadedState,

    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
      }),

    // middleware: (getDefaultMiddleware) =>
    //   getDefaultMiddleware(
    //     {
    //       serializableCheck: {
    //         // Ignore these action types
    //         ignoredActions: [web3Actions.web3ConnectGanacheSucceed.toString(), web3Actions.metaMaskConnectSuccess.toString()],
    //         // Ignore these paths in the state
    //         ignoredPaths: ['web3State.web3', 'web3State.web3']
    //       }
    //     }
    //     ).concat(api.middleware).concat(sagaMiddleware).concat(thunk),
  })
}

export type OtStore = typeof store

export function assertRole(actualRole: RoleEnum | undefined, expectedRole: RoleEnum | RoleEnum[]) {
  if (!actualRole) {
    throw new Error('Role is not defined')
  }
  if (Array.isArray(expectedRole)) {
    if (!expectedRole.includes(actualRole)) {
      throw new Error(
        `Role ${actualRole} is not allowed to perform this action. Expected one of ${expectedRole.join(', ')}`
      )
    }
  } else {
    if (actualRole !== expectedRole) {
      throw new Error(`Role ${actualRole} is not allowed to perform this action. Expected ${expectedRole}`)
    }
  }
}

//

// Infer the `RootState` and `AppDispatch` types from the store itself

// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type OtDispatch = OtStore['dispatch'] & Dispatch<ThunkAction<void, OtStoreState, unknown, UnknownAction>>
export const useOtDispatch = useDispatch.withTypes<OtDispatch>()
export const useOtSelector = useSelector.withTypes<OtStoreState>()

export const useOtChainState = (): ChainStateInitialized => {
  const chainState = useOtSelector((state) => state.chainState)
  if (!chainState.userConnection) {
    return {
      ...chainState,
      userConnection: {} as UserConnection,
    }
  }
  return chainState as ChainState & { userConnection: UserConnection }
}

export const useOtStore: () => OtStore = useStore

export async function dispatchError(
  dispatch: ThunkDispatch<OtStoreState, unknown, UnknownAction>,
  err: EthersError | UserError | Error,
  activity: ActivityEnum,
  errorNumber: number
) {
  let stack: string | undefined = 'no stack'
  if ('stack' in err) {
    stack = err.stack
  } else if ('error' in err && err.error) {
    if ('stack' in err.error) {
      stack = err.error.stack
    }
  }
  logOT.info('handleError', errorNumber, activity, stack)
  const alertCTO: AlertCTO = alertCTOFromError(err, activity)
  alertCTO.errorNumber = errorNumber
  dispatch(alertActions.addAlert(alertCTO))
  logOT.error('Error:', alertCTO)
}

export const useOtChainTimestamp = () => {
  const [currentTimestamp, setCurrentTimestamp] = useState<number>(Math.round(Date.now() / 1000))
  const {
    chainConfiguration: { chainTimestampDelta },
  } = useOtChainState()
  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTimestamp(Math.round(Date.now() / 1000))
    }, 60000) // Update every 60 second to keep current timestamp fresh

    return () => clearInterval(intervalId) // Clean up interval on unmount
  }, [])

  // Return the sum of the current timestamp and chainstate.chaintimestamp
  return currentTimestamp + chainTimestampDelta
}
